/**
 * VGP Local Artist Puzzles Page
 * Fetches and displays products linked to artists with geo assignments.
 * BUILD082 M4 - D13, D14
 *
 * Pattern: Based on vgp-localpuzzles.js, using /geo/local-artist-products endpoint.
 * Dynamic heading: "Local Artist Puzzles - [Region Name]" (D14).
 */
(function() {
  'use strict';

  // Ensure utilities are loaded
  if (typeof window.VGPProductUtils === 'undefined') {
    console.error('[VGPLocalArtistPuzzles] VGPProductUtils not loaded. Cannot initialize.');
    return;
  }

  var Utils = window.VGPProductUtils;
  var CACHE_KEY_PREFIX = 'vgp_local_artist_products_';
  var CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes (D10)
  var PRODUCTS_PER_PAGE = 24;
  var CONFIG = {
    proxyApiUrl: '/apps/vgp-wholesale',
    endpoint: '/geo/local-artist-products',
    detectEndpoint: '/geo/detect',
    timeoutMs: 8000,
    debug: true
  };

  function log() {
    if (CONFIG.debug) console.log.apply(console, ['[VGPLocalArtistPuzzles]'].concat(Array.prototype.slice.call(arguments)));
  }

  function logError() {
    console.error.apply(console, ['[VGPLocalArtistPuzzles]'].concat(Array.prototype.slice.call(arguments)));
  }

  // State
  var currentPage = 1;
  var isLoading = false;
  var hasMore = false;
  var regionId = null;
  var abortController = null;

  // DOM elements
  var pageContainer = null;
  var gridContainer = null;
  var emptyState = null;
  var loadMoreBtn = null;
  var countDisplay = null;
  var headingEl = null;
  var apiBaseUrl = '';

  /**
   * Initialize the module
   */
  async function init() {
    pageContainer = document.querySelector('[data-vgp-local-artist-puzzles-page]');
    if (!pageContainer) return; // Not on local artist puzzles page

    gridContainer = document.querySelector('[data-vgp-local-artist-puzzles-grid]');
    emptyState = document.querySelector('[data-vgp-empty-state]');
    loadMoreBtn = document.querySelector('[data-vgp-load-more]');
    countDisplay = document.querySelector('[data-vgp-product-count]');
    headingEl = document.querySelector('[data-vgp-artist-puzzles-heading]');
    apiBaseUrl = pageContainer.getAttribute('data-vgp-direct-api-url') || '';

    if (!apiBaseUrl) {
      logError('No API URL configured.');
      return;
    }

    // Show skeleton loading
    if (gridContainer) {
      gridContainer.innerHTML = Utils.createSkeletonGrid(8);
    }

    // Cancel any in-flight requests
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    // Reset state
    currentPage = 1;
    hasMore = false;
    regionId = null;

    // Try to reuse region from nav filter (avoids duplicate /geo/detect API call)
    var storedRegion = null;
    try { storedRegion = sessionStorage.getItem('vgp_detected_region_id'); }
    catch (e) { /* sessionStorage not available */ }

    if (storedRegion) {
      regionId = parseInt(storedRegion, 10);
      log('Using cached region from nav filter:', regionId);
    } else {
      var detected = await Utils.detectRegion(CONFIG.proxyApiUrl, CONFIG.detectEndpoint);
      if (detected) {
        regionId = detected;
        log('Detected region:', regionId);
      }
    }

    // Load first page
    await loadProducts(1, false);

    // Setup load more button (use onclick to avoid duplicate listeners on re-init)
    if (loadMoreBtn) {
      loadMoreBtn.onclick = handleLoadMore;
    }
  }

  /**
   * Build cache key
   */
  function getCacheKey(page) {
    return CACHE_KEY_PREFIX + 'region_' + (regionId || 'none') + '_page_' + page;
  }

  /**
   * Build API URL
   */
  function buildApiUrl(page) {
    var baseUrl;
    if (Utils.hasWholesaleJWT()) {
      baseUrl = CONFIG.proxyApiUrl;
    } else {
      baseUrl = apiBaseUrl;
    }

    var url = baseUrl + CONFIG.endpoint + '?page=' + page + '&limit=' + PRODUCTS_PER_PAGE;
    if (regionId) url += '&region_id=' + regionId;
    return url;
  }

  /**
   * Update heading with region name (D14)
   * REACT033: Disabled — heading stays as static Liquid text "Local Artist Puzzles"
   */
  function updateHeading(_regionName) {
    // no-op: region name no longer appended to heading
  }

  /**
   * Load products from API
   */
  async function loadProducts(page, append) {
    if (isLoading) return;
    isLoading = true;

    if (loadMoreBtn) {
      loadMoreBtn.disabled = true;
      loadMoreBtn.textContent = 'Loading...';
    }

    try {
      // Check cache
      var cacheKey = getCacheKey(page);
      var data = Utils.getCached(cacheKey, CACHE_TTL_MS);

      if (!data) {
        // Fetch from API
        var url = buildApiUrl(page);
        log('Fetching:', url);

        // Timeout: abort after configured ms
        var timeoutId = setTimeout(function() {
          if (abortController) abortController.abort();
        }, CONFIG.timeoutMs);

        var response;
        try {
          response = await fetch(url, {
            headers: Utils.buildAuthHeaders(),
            signal: abortController ? abortController.signal : undefined
          });
        } finally {
          clearTimeout(timeoutId);
        }

        if (!response.ok) {
          throw new Error('API error: ' + response.status);
        }

        data = await response.json();
        Utils.setCache(cacheKey, data, CACHE_TTL_MS);
      }

      // D14: Update heading with region name on first load
      if (!append && data.region && data.region.name) {
        updateHeading(data.region.name);
      }

      // Render products
      renderProducts(data.products || [], append);

      // Update state
      currentPage = page;
      hasMore = data.pagination ? data.pagination.has_more : false;

      // Update count display
      if (countDisplay && data.pagination) {
        var showing = append
          ? gridContainer.querySelectorAll('[data-vgp-product-card]').length
          : (data.products || []).length;
        countDisplay.textContent = showing + ' of ' + data.pagination.total + ' products';
      }

      // Show/hide load more
      if (loadMoreBtn) {
        loadMoreBtn.style.display = hasMore ? 'block' : 'none';
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Load More';
      }

      // Show empty state if no products
      if (!append && (!data.products || data.products.length === 0)) {
        if (gridContainer) gridContainer.innerHTML = '';
        if (emptyState) emptyState.style.display = 'block';
      } else if (emptyState) {
        emptyState.style.display = 'none';
      }

    } catch (error) {
      if (error.name === 'AbortError') return;

      logError('Failed to load products:', error.message);

      if (!append && gridContainer) {
        gridContainer.innerHTML =
          '<div class="local-artist-puzzles-empty" style="grid-column: 1 / -1;">' +
            '<h3>Unable to load products</h3>' +
            '<p>Unable to load products. Please try refreshing the page.</p>' +
          '</div>';
      }
    } finally {
      isLoading = false;
    }
  }

  /**
   * Render products to grid
   */
  function renderProducts(products, append) {
    if (!gridContainer) return;
    var html = products.map(function(p) { return Utils.renderProductCard(p); }).join('');

    if (append) {
      gridContainer.insertAdjacentHTML('beforeend', html);
    } else {
      gridContainer.innerHTML = html;
    }
  }

  /**
   * Handle Load More click
   */
  async function handleLoadMore() {
    if (isLoading || !hasMore) return;
    await loadProducts(currentPage + 1, true);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(init, 150);
    });
  } else {
    setTimeout(init, 150);
  }

  // Re-initialize on auth change
  window.addEventListener('vgp:authenticated', function() {
    log('Auth event received, re-initializing');
    // Clear cached responses so re-fetch uses authenticated segment
    try {
      Object.keys(sessionStorage).forEach(function(key) {
        if (key.indexOf(CACHE_KEY_PREFIX) === 0) sessionStorage.removeItem(key);
      });
    } catch(e) {}
    currentPage = 1;
    hasMore = false;
    regionId = null;
    init();
  });

  log('Module loaded');
})();